// @ts-strict-ignore
import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';

import {FlowsQueryEventEntry} from 'common/query-builder/query-entry-section/types';

import {SankeyNode} from './sankey-node';
import {SankeyEdge} from './sankey-edge';
import {SankeyNodeType, SankeyNodeTypePriority, SankeyStepType} from './types';
import {SankeyStep} from './sankey-step';
import {SankeyBreakdown} from './sanky-breakdown';
import {Metadata, SerializedSankeyFlow} from './serialized';

export class SankeyFlow {
  public declare breakdowns: SankeyBreakdown[];
  public declare computedAt: string;
  public declare metadata: Metadata;
  public declare steps: SankeyStep[];

  constructor(
    // eslint-disable-next-line @typescript-eslint/naming-convention
    {breakdowns, computedAt, computed_at, metadata, steps}: SerializedSankeyFlow,
    querySteps: FlowsQueryEventEntry[],
    alignment?: number[],
  ) {
    this.breakdowns = (breakdowns || []).map((b) => new SankeyBreakdown(b));
    this.computedAt = computedAt || computed_at;
    this.metadata = metadata && {
      minSamplingFactor: metadata[`min_sampling_factor`],
    };
    // The type of this step FORWARD/REVERSE/ANCHOR
    const stepTypes: SankeyStepType[] = [];

    // The anchor step associated with this step, i.e if it is a forward or reverse step
    // of an anchor.
    const stepAnchor = [];

    // The index of the anchor associated with this step
    const anchorStepIndex = [];

    const anchorStepIndexToQueryStepIndex = new Map<number, number>();
    const queryStepIndexToAnchorStepIndex = new Map<number, number>();

    for (let i = 0; i < querySteps.length; i++) {
      const numReverseSteps = querySteps[i].reverse || 0;
      stepTypes.push(...Array(numReverseSteps).fill(SankeyStepType.Reverse));
      stepAnchor.push(...Array(numReverseSteps).fill(i));

      stepTypes.push(SankeyStepType.Anchor);
      stepAnchor.push(i);
      const anchorIndex = stepAnchor.length - 1;

      const numForwardSteps = querySteps[i].forward || 0;
      stepTypes.push(...Array(numForwardSteps).fill(SankeyStepType.Forward));
      stepAnchor.push(...Array(numForwardSteps).fill(i));
      anchorStepIndex.push(...Array(numReverseSteps + numForwardSteps + 1).fill(anchorIndex));
      anchorStepIndexToQueryStepIndex.set(anchorIndex, i);
      queryStepIndexToAnchorStepIndex.set(i, anchorIndex);
    }

    this.steps = steps.map((step, stepNumber) => {
      const canAddForwardStep = this.canAddForwardStep(stepNumber, stepTypes);
      const canAddReverseStep = this.canAddReverseStep(stepNumber, stepTypes);
      const queryStep = querySteps[stepAnchor[stepNumber]];
      const anchorIndex = anchorStepIndex[stepNumber];
      const queryStepIndex = anchorStepIndexToQueryStepIndex.get(anchorIndex);
      const nextAnchorIndex =
        queryStepIndex < querySteps.length - 1 ? queryStepIndexToAnchorStepIndex.get(queryStepIndex + 1) : undefined;
      return new SankeyStep(
        step.nodes,
        stepNumber,
        canAddForwardStep,
        canAddReverseStep,
        stepTypes[stepNumber],
        anchorIndex,
        nextAnchorIndex,
        stepAnchor[stepNumber],
        alignment,
        queryStep,
      );
    });

    // fill edge information for all nodes in the flow
    this.fillFlowEdges();
  }

  /**
   * Returns the first step in the flow considered an anchor step
   */
  public getFirstAnchorStep() {
    return this.steps.find((step) => step.nodes.some((node) => node.type === SankeyNodeType.Anchor));
  }

  /**
   * Calculates the top path for incoming and outgoing
   * edges for a given start node, represented as a
   * set of edge ids
   */
  public calculateTopFlowPath(startNode) {
    const path = new Set();

    // add incoming edges if startNode type is a drop-off node
    // otherwise add all outgoing edges
    if (startNode.type === SankeyNodeType.Dropoff) {
      startNode.incomingEdges.forEach((edge) => path.add(edge.edgeId));
    } else {
      startNode.edges.forEach((edge) => path.add(edge.edgeId));
    }

    // highlight incoming and outgoing top paths
    this.findNodeTopEdges(path, startNode, `from`);
    this.findNodeTopEdges(path, startNode, `to`);

    return path;
  }

  // A step can add a reverse step depending on what type of step it is and the precceding step
  // for e.g you can always add a reverse step between two achors but cannot add a reverse step
  // between two forard steps.
  private canAddReverseStep(stepNumber: number, stepTypes: SankeyStepType[]): boolean {
    if (stepNumber === 0) {
      return true;
    } else {
      const stepType = stepTypes[stepNumber];
      const prevStepType = stepTypes[stepNumber - 1];
      return (
        (prevStepType === SankeyStepType.Anchor && stepType === SankeyStepType.Anchor) ||
        (prevStepType === SankeyStepType.Forward && stepType === SankeyStepType.Anchor) ||
        (prevStepType === SankeyStepType.Anchor && stepType === SankeyStepType.Reverse) ||
        (stepType === SankeyStepType.Reverse && prevStepType === SankeyStepType.Forward)
      );
    }
  }

  // A step can add a forward step depending on what type of step it is and the next step
  // for e.g you can always add a forward step between two achors but cannot add a forward step
  // between two reverse steps.
  private canAddForwardStep(stepNumber: number, stepTypes: SankeyStepType[]): boolean {
    if (stepNumber === stepTypes.length - 1) {
      return true;
    } else {
      const stepType = stepTypes[stepNumber];
      const nextStepType = stepTypes[stepNumber + 1];
      return (
        (nextStepType === SankeyStepType.Anchor && stepType === SankeyStepType.Anchor) ||
        (nextStepType === SankeyStepType.Anchor && stepType === SankeyStepType.Forward) ||
        (nextStepType === SankeyStepType.Reverse && stepType === SankeyStepType.Anchor) ||
        (nextStepType === SankeyStepType.Reverse && stepType === SankeyStepType.Forward)
      );
    }
  }

  private findNodeTopEdges(path, node, direction): void {
    const edgesPropertyName = direction === `from` ? `incomingEdges` : `edges`;
    const nodePropertyName = direction === `from` ? `fromNode` : `toNode`;
    const topEdge = this.findTopEdge(node[edgesPropertyName], nodePropertyName);

    if (topEdge) {
      let currentNode = topEdge[nodePropertyName];
      path.add(topEdge.edgeId);

      while (currentNode) {
        const currentNodeTopEdge = this.findTopEdge(currentNode[edgesPropertyName], nodePropertyName);

        if (!currentNodeTopEdge) {
          break;
        }

        path.add(currentNodeTopEdge.edgeId);
        currentNode = currentNodeTopEdge[nodePropertyName];
      }
    }
  }

  private findTopEdge(edges, nodePropertyName): SankeyEdge {
    const sortedEdges = orderBy(
      edges,
      [
        // consider event to event edges first
        (edge) => SankeyNodeTypePriority[edge[nodePropertyName].type],
        // consider edges with greatest weight first
        `totalCount`,
      ],
      [`desc`, `desc`],
    );

    return sortedEdges.length ? sortedEdges[0] : null;
  }

  private findNode(stepIndex, id): SankeyNode {
    const step = this.steps[stepIndex];
    return step.nodes.find((node) => node.id === id);
  }

  private fillFlowEdges() {
    const nodes = flatten(this.steps.map((step) => step.nodes));
    for (const node of nodes) {
      for (const edge of node.edges) {
        const targetNode = this.findNode(edge.step, edge.id);
        edge.fromNode = node;
        edge.toNode = targetNode;

        targetNode.addIncomingEdge(edge);
      }

      node.sortEdges();
    }
  }
}
